import React, { createContext, useState, useCallback, useEffect, useRef } from 'react';
import { onAuthStateChanged, signOut, getAuth } from 'firebase/auth';
import { doc, getFirestore, collection, query, getDocs, where, onSnapshot } from 'firebase/firestore';
import { ref as firebaseRef, listAll, getDownloadURL } from 'firebase/storage';

import { storage } from '../firebase/firebase';
import { crearRedireccionUnica } from '../utils/loginRedirectGate';

const AppContext = createContext();

const AppProvider = ({ children }) => {
  const [isAuth, setIsAuth] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [lecciones, setLecciones] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [evaluaciones, setEvaluaciones] = useState([]);
  const [lecturasAdicionales, setLecturasAdicionales] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [globalLoadingCount, setGlobalLoadingCount] = useState(0);
  const [justLoggedIn, setJustLoggedIn] = useState(false);
  const [level, setUserLevel] = useState(null);
  const [teacher, setTeacher] = useState(false);
  const [audios, setAudios] = useState([]);
  const [currentAudioIndex, setCurrentAudioIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isAudioPlayerActive, setIsAudioPlayerActive] = useState(false);
  const [currentAudioTime, setCurrentAudioTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const audioRef = useRef(null);
  const authInitializedRef = useRef(false);
  const wasAuthenticatedRef = useRef(false);

  const auth = getAuth();
  const db = getFirestore();
  const uidSesion = currentUser?.uid;
  const esMasterSesion = currentUser?.isMaster === true;

  useEffect(() => {
    setIsLoading(true);
    let unsubscribeUser = null;

    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const consumirRedireccionLogin = crearRedireccionUnica(
          authInitializedRef.current && !wasAuthenticatedRef.current
        );
        wasAuthenticatedRef.current = true;
        const userDocRef = doc(db, 'users', user.uid);
        
        // Listener en tiempo real para los datos del usuario
        unsubscribeUser = onSnapshot(userDocRef, (docSnap) => {
          const userData = docSnap.exists()
            ? { uid: user.uid, ...docSnap.data() }
            : { uid: user.uid };

          const normalizedLevel = userData.level?.toString().replace('Nivel ', '') || null;

          setCurrentUser(userData);
          if (consumirRedireccionLogin()) {
            setJustLoggedIn(true);
          }
          setIsAuth(true);
          setUserLevel(normalizedLevel);
          setTeacher(userData.teacher || false);
          setIsLoading(false);
        }, (error) => {
          console.error('Error listening to user data:', error);
          setCurrentUser({ uid: user.uid });
          setIsAuth(true);
          setIsLoading(false);
        });
      } else {
        if (unsubscribeUser) unsubscribeUser();
        setCurrentUser(null);
        setIsAuth(false);
        wasAuthenticatedRef.current = false;
        setJustLoggedIn(false);
        setLecciones([]);
        setMaterials([]);
        setEvaluaciones([]);
        setLecturasAdicionales([]);
        setUserLevel(null);
        setTeacher(false);
        setIsPlaying(false);
        setIsAudioPlayerActive(false);
        setCurrentAudioTime(0);
        setAudioDuration(0);
        setIsLoading(false);
      }

      authInitializedRef.current = true;
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeUser) unsubscribeUser();
    };
  }, [auth, db]);

  const fetchData = useCallback(async (collectionOrQuery, setter) => {
    try {
      const querySnapshot = await getDocs(collectionOrQuery);
      const items = querySnapshot.docs.map((docSnapshot) => ({
        id: docSnapshot.id,
        ...docSnapshot.data(),
      }));
      setter(items);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, []);

  useEffect(() => {
    if (isAuth && uidSesion) {
      if (teacher || esMasterSesion) {
        fetchData(collection(db, 'Lecciones'), setLecciones);
        fetchData(collection(db, 'materials'), setMaterials);
        fetchData(collection(db, 'lecturasAdicionales'), setLecturasAdicionales);
      } else if (level) {
        const lvStr = level.toString();
        // Para alumnos, solo traemos lecciones y materiales hasta su nivel
        fetchData(query(collection(db, 'Lecciones'), where('level', '<=', lvStr)), setLecciones);
        fetchData(query(collection(db, 'materials'), where('level', '<=', lvStr)), setMaterials);
        // Las lecturas adicionales para alumnos se manejan por asignación específica en su componente
        setLecturasAdicionales([]);
      }
    }
  }, [fetchData, isAuth, teacher, esMasterSesion, uidSesion, level, db]);

  const fetchEvaluacionesPorNivel = useCallback(async (nivel) => {
    const evaluacionesQuery = query(collection(db, 'evaluaciones'), where('nivel', '==', nivel));

    try {
      const querySnapshot = await getDocs(evaluacionesQuery);
      const items = querySnapshot.docs.map((docSnapshot) => ({
        id: docSnapshot.id,
        ...docSnapshot.data(),
      }));
      setEvaluaciones(items);
    } catch (error) {
      console.error('Error fetching evaluations:', error);
      setEvaluaciones([]);
    }
  }, [db]);

  const logOut = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const showGlobalLoader = useCallback(() => {
    setGlobalLoadingCount((count) => count + 1);
  }, []);

  const hideGlobalLoader = useCallback(() => {
    setGlobalLoadingCount((count) => Math.max(0, count - 1));
  }, []);

  useEffect(() => {
    const fetchAudioURLs = async () => {
      if (!storage) {
        setAudios([]);
        setIsPlaying(false);
        setIsAudioPlayerActive(false);
        return;
      }

      try {
        const audioList = [];
        const audioFolderRef = firebaseRef(storage, 'music/');
        const { items } = await listAll(audioFolderRef);

        for (let item of items) {
          const url = await getDownloadURL(item);
          audioList.push({ name: item.name, url });
        }

        setAudios(audioList);
      } catch (err) {
        console.error('Error listing music/ from Storage:', err);
        setAudios([]);
        setIsPlaying(false);
        setIsAudioPlayerActive(false);
      }
    };

    fetchAudioURLs();
  }, []);

  const currentAudio = audios[currentAudioIndex] || null;

  const activateAudioPlayer = useCallback(() => {
    if (!audios.length) return;
    setIsAudioPlayerActive(true);
    setIsPlaying(true);
  }, [audios.length]);

  const playNext = useCallback(() => {
    if (!audios.length) return;
    setCurrentAudioIndex((index) => (index + 1) % audios.length);
    setCurrentAudioTime(0);
    setIsAudioPlayerActive(true);
    setIsPlaying(true);
  }, [audios.length]);

  const playPrev = useCallback(() => {
    if (!audios.length) return;
    setCurrentAudioIndex((index) => (index === 0 ? audios.length - 1 : index - 1));
    setCurrentAudioTime(0);
    setIsAudioPlayerActive(true);
    setIsPlaying(true);
  }, [audios.length]);

  const togglePlayPause = useCallback(() => {
    if (!audios.length) return;
    setIsAudioPlayerActive(true);
    setIsPlaying((playing) => !playing);
  }, [audios.length]);

  const selectAudio = useCallback((index, shouldPlay = true) => {
    if (index < 0 || index >= audios.length) return;

    setCurrentAudioIndex(index);
    setCurrentAudioTime(0);
    setIsAudioPlayerActive(true);
    setIsPlaying(shouldPlay);
  }, [audios.length]);

  const seekAudio = useCallback((nextTime) => {
    const parsedTime = Number(nextTime);
    const safeTime = Number.isFinite(parsedTime) ? parsedTime : 0;

    if (audioRef.current) {
      audioRef.current.currentTime = safeTime;
    }

    setCurrentAudioTime(safeTime);
  }, []);

  const closeAudioPlayer = useCallback(() => {
    setIsPlaying(false);
    setIsAudioPlayerActive(false);
    setCurrentAudioTime(0);
    setAudioDuration(0);

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, []);

  return (
    <AppContext.Provider value={{
      isAuth,
      setIsAuth,
      currentUser,
      logOut,
      lecciones,
      materials,
      teacher,
      level,
      evaluaciones,
      fetchEvaluacionesPorNivel,
      lecturasAdicionales,
      audios,
      currentAudio,
      currentAudioIndex,
      isPlaying,
      isAudioPlayerActive,
      currentAudioTime,
      audioDuration,
      activateAudioPlayer,
      playNext,
      playPrev,
      togglePlayPause,
      selectAudio,
      seekAudio,
      closeAudioPlayer,
      audioRef,
      setCurrentAudioTime,
      setAudioDuration,
      isLoading,
      isGlobalLoading: globalLoadingCount > 0,
      showGlobalLoader,
      hideGlobalLoader,
      justLoggedIn,
      setJustLoggedIn,
    }}>
      {children}
    </AppContext.Provider>
  );
};

export { AppContext, AppProvider };
